const DEFAULT_MAX_ATTEMPTS = 3
const DEFAULT_BASE_DELAY_MS = 1000

function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Determina si un fallo es transitorio (red, timeout, 5xx) y merece reintento.
 * No reintenta errores de negocio (status:false con respuesta HTTP 200) excepto NOT_PERSISTED.
 */
function isRetryableFailure(result, error) {
    if (error) {
        if (error.status === false && error.msg === 'Error: Api Error') {
            const httpStatus =
                error.httpStatus ||
                (error.response && error.response.status ? error.response.status : null)

            return httpStatus !== null && httpStatus >= 500
        }

        const httpStatus = error.response && error.response.status ? error.response.status : null
        if (httpStatus && httpStatus >= 500) {
            return true
        }
        if (error.code === 'ECONNABORTED') {
            return true
        }
        if (!error.response) {
            return true
        }
        return false
    }

    if (!result || result.status) {
        return false
    }
    if (result.code && result.code === 'NOT_PERSISTED') {
        return true
    }

    const msg = String(result.msg || '')
    return msg.includes('Network Error') || msg.toLowerCase().includes('timeout')
}

/**
 * Indica si un fallo debe encolarse para sync offline.
 * Cubre errores de red, NOT_PERSISTED, 5xx del wrapper Api tras reintentos agotados.
 * Los 4xx y rechazos de negocio HTTP 200 no deben encolarse.
 */
export function shouldQueueOfflineSync(response) {
    if (!response || response.status) {
        return false
    }

    if (response.code === 'NOT_PERSISTED') {
        return true
    }

    const msg = String(response.msg || '')

    if (msg === 'Error: Api Error') {
        const httpStatus = response.httpStatus || null
        return httpStatus !== null && httpStatus >= 500
    }

    if (msg.includes('Network Error') || msg.toLowerCase().includes('timeout')) {
        return true
    }

    return false
}

/**
 * POST con reintentos para acciones de checklist (answer, reset, comment).
 * Envía `attempt` (1..maxAttempts) en cada petición.
 *
 * @returns {Promise<{ response: object, attempt: number, success: boolean }>}
 */
export async function postTaskWithRetry(api, endpoint, params, options = {}) {
    const maxAttempts = options.maxAttempts ? options.maxAttempts : DEFAULT_MAX_ATTEMPTS
    const baseDelayMs = options.baseDelayMs ? options.baseDelayMs : DEFAULT_BASE_DELAY_MS
    let lastResponse = { status: false }

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        const payload = { ...params, attempt: String(attempt) }

        try {
            const response = await api.post(endpoint, payload)
            lastResponse = response || { status: false }

            if (response && response.status) {
                return { response, attempt, success: true }
            }

            log('response', response)
            log('attempt', attempt)
            log('maxAttempts', maxAttempts)
            log('isRetryableFailure', isRetryableFailure(response, null))
            if (attempt < maxAttempts && isRetryableFailure(response, null)) {
                await sleep(baseDelayMs * attempt)
                continue
            }

            return { response: lastResponse, attempt, success: false }
        } catch (error) {
            const rejectedResponse =
                error && typeof error === 'object' && error.status === false
                    ? error
                    : { status: false, msg: error && error.msg ? error.msg : 'Error: Api Error' }

            lastResponse = rejectedResponse

            if (attempt < maxAttempts && isRetryableFailure(null, error)) {
                await sleep(baseDelayMs * attempt)
                continue
            }

            return { response: lastResponse, attempt, success: false }
        }
    }

    return { response: lastResponse, attempt: maxAttempts, success: false }
}

export { DEFAULT_MAX_ATTEMPTS, DEFAULT_BASE_DELAY_MS }
